export type ShareMeta = {
  title: string;
  description: string;
};

const SITE = 'Talos Badges';
const DEFAULT_DESCRIPTION = 'View and Collect Badges!';

/** Keep in sync with `RESERVED_PATHS` in nav-paths (no React / DOM — edge-safe). */
const RESERVED_PATHS = new Set([
  'gallery',
  'journeys',
  'stats',
  'leaderboard',
  'explore',
  'create',
  'claim',
  'edit',
  'summary',
  'feed',
  'battle',
]);

const isContributionsSegment = (seg: string | undefined): boolean => seg === 'contributions' || seg === 'submitted';

const withSite = (page: string): string => `${SITE} ${page}`;

/**
 * URL profile id → implied display name for share titles.
 */
export const formatProfileIdForShare = (profileId: string): string =>
  profileId
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

/** Possessive profile label — title owns the who; description owns the what (no name repeat). */
const profilePossessive = (profileId: string, page: string, displayName?: string): string => {
  const who = displayName?.trim() || formatProfileIdForShare(profileId);
  return `${who}'s ${page}`;
};

export type ShareMetaOptions = {
  /** Logged-in client: real sheet display name. Edge/crawlers omit this and use the URL id. */
  profileDisplayName?: string;
  /** True once logged in and profiles have finished loading — feed title becomes “Talos Badges Feed”. */
  isAuthenticated?: boolean;
};

/**
 * Open Graph / document title + description from a URL pathname.
 * Used by the client (browser tab) and Netlify edge (Slack/Discord crawlers).
 * Path-only by default — pass `profileDisplayName` when profiles are loaded in-app.
 */
export const shareMetaForPath = (pathname: string, options?: ShareMetaOptions): ShareMeta => {
  const parts = pathname.split('/').filter(Boolean);
  const [seg0, seg1] = parts;
  const displayName = options?.profileDisplayName;
  const feedTitle = options?.isAuthenticated ? withSite('Feed') : SITE;

  if (!seg0) {
    return {
      title: feedTitle,
      description: DEFAULT_DESCRIPTION,
    };
  }

  if (seg0 === 'battle') {
    if (seg1 === 'how-to-play') {
      return {
        title: withSite('How to Play'),
        description: 'How to play Battle Badges.',
      };
    }
    if (seg1 === 'arena') {
      return {
        title: withSite('Battle Arena'),
        description: 'Draft a deck and run the Battle Arena.',
      };
    }
    if (seg1 === 'leaderboard') {
      return {
        title: withSite('Leaderboard'),
        description: 'CPU win leaderboard for Battle Badges.',
      };
    }
    if (seg1 === 'badges') {
      return {
        title: `${SITE} · Battle Badges`,
        description: 'Battle Badges on Talos Badges!',
      };
    }
    return {
      title: `${SITE} · Battle Badges`,
      description: seg1 ? 'Join this Battle on Talos Badges' : 'Battle Badges on Talos Badges!',
    };
  }

  if (!seg1) {
    switch (seg0) {
      case 'stats':
        return {
          title: withSite('Stats'),
          description: 'Badges Stats on Talos Badges.',
        };
      case 'leaderboard':
        return {
          title: withSite('Leaderboard'),
          description: 'The Leaderboard on Talos Badges.',
        };
      case 'explore':
        return {
          title: withSite('Find Profile'),
          description: 'Find a profile on Talos Badges.',
        };
      case 'create':
        return {
          title: withSite('Create Profile'),
          description: 'Create a profile on Talos Badges.',
        };
      case 'claim':
      case 'edit':
        return {
          title: 'Claim New Badges',
          description: 'Claim badges on Talos Badges.',
        };
      case 'summary':
        return {
          title: 'Summarize & Claim',
          description: 'Review and claim badges on Talos Badges.',
        };
      case 'gallery':
        return {
          title: withSite('Gallery'),
          description: 'The Badges Gallery on Talos Badges.',
        };
      case 'feed':
        return {
          title: feedTitle,
          description: DEFAULT_DESCRIPTION,
        };
      default:
        if (RESERVED_PATHS.has(seg0)) {
          return { title: SITE, description: DEFAULT_DESCRIPTION };
        }
        return {
          title: profilePossessive(seg0, 'Badges', displayName),
          description: 'Badges on Talos Badges.',
        };
    }
  }

  if (seg0 === 'gallery') {
    if (seg1 === 'journeys') {
      return {
        title: withSite('Journeys'),
        description: 'The Journeys Gallery on Talos Badges.',
      };
    }
    return {
      title: withSite('Gallery'),
      description: 'The Badges Gallery on Talos Badges.',
    };
  }

  if (seg0 === 'stats' && seg1 === 'hashes') {
    return {
      title: withSite('Badge Hashes'),
      description: 'Unique Badge Hashes on Talos Badges.',
    };
  }

  if (RESERVED_PATHS.has(seg0)) {
    return { title: SITE, description: DEFAULT_DESCRIPTION };
  }

  const profileId = seg0;
  if (isContributionsSegment(seg1)) {
    return {
      title: profilePossessive(profileId, 'Contributions', displayName),
      description: 'Contributions to Talos Badges.',
    };
  }
  if (seg1 === 'journeys') {
    return {
      title: profilePossessive(profileId, 'Journeys', displayName),
      description: 'Journeys on Talos Badges.',
    };
  }
  if (seg1 === 'feed') {
    return {
      title: profilePossessive(profileId, 'Feed', displayName),
      description: 'Feed on Talos Badges.',
    };
  }
  if (seg1 === 'claim' || seg1 === 'edit') {
    return {
      title: 'Claim New Badges',
      description: 'Claim badges on Talos Badges.',
    };
  }

  return {
    title: profilePossessive(profileId, 'Badges', displayName),
    description: 'Badges on Talos Badges.',
  };
};
